"use client";

import {
  FileText,
  Shield,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  FeatureUnifiedIcon,
  FeatureAvailabilityIcon,
  FeaturePricingIcon,
  ReceiptGenerationIcon,
  SignatureVerificationIcon,
} from "@/components/icons/FeatureIcons";

const capabilities = [
  {
    label: "יצירת קבלות",
    description: "מפיקים קבלות PDF עם חתימה דיגיטלית מכל מערכת שיש לכם.",
    icon: FileText,
    visualIcon: ReceiptGenerationIcon,
  },
  {
    label: "בדיקת תוקף",
    description: "מוודאים שקבלה אמיתית ישר דרך הקוד. בלי לפתוח אתר.",
    icon: Shield,
    visualIcon: SignatureVerificationIcon,
  },
];

const features = [
  {
    title: "ממשק אחד",
    description: "יצירת קבלות, בדיקת חתימות, התראות. הכל מאותו URL.",
    icon: FeatureUnifiedIcon,
  },
  {
    title: "לא נתקעים",
    description: "אם הבקשה נכשלה, המערכת מנסה שוב לבד. תמיד.",
    icon: FeatureAvailabilityIcon,
  },
  {
    title: "שקוף",
    description: "משלמים על קבלות שנשלחו, לא על בקשות או על API.",
    icon: FeaturePricingIcon,
  },
];

export function ApiPageContent() {
  const [activeCapability, setActiveCapability] = useState(0);
  const [progress, setProgress] = useState(0);
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
  // Separate pause states for capabilities and carousel (they work independently)
  const [isCapabilitiesPaused, setIsCapabilitiesPaused] = useState(false);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const capabilitiesTimerRef = useRef<NodeJS.Timeout | null>(null);
  const carouselTimerRef = useRef<NodeJS.Timeout | null>(null);
  const ActiveVisualIcon = capabilities[activeCapability].visualIcon;

  // Auto-cycle through capabilities every 8 seconds (paused when user interacts)
  useEffect(() => {
    if (isCapabilitiesPaused) return;

    const interval = setInterval(() => {
      setActiveCapability((prev) => (prev + 1) % capabilities.length);
      setProgress(0);
    }, 8000);

    return () => clearInterval(interval);
  }, [isCapabilitiesPaused]);

  // Progress animation (paused when user interacts with capabilities)
  useEffect(() => {
    if (isCapabilitiesPaused) return;

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 0;
        return prev + 100 / (8000 / 30); // Update every 30ms
      });
    }, 30);

    return () => clearInterval(progressInterval);
  }, [isCapabilitiesPaused]);

  // Auto-cycle through features carousel on mobile (paused when user interacts)
  useEffect(() => {
    if (isCarouselPaused) return;

    const interval = setInterval(() => {
      setActiveFeatureIndex((prev) => (prev + 1) % features.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isCarouselPaused]);

  // Pause capabilities with auto-resume after 5 seconds
  const pauseCapabilitiesWithResume = useCallback(() => {
    setIsCapabilitiesPaused(true);

    // Clear any existing timer
    if (capabilitiesTimerRef.current) {
      clearTimeout(capabilitiesTimerRef.current);
    }

    // Set new timer to resume after 5 seconds
    capabilitiesTimerRef.current = setTimeout(() => {
      setIsCapabilitiesPaused(false);
    }, 5000);
  }, []);

  // Pause carousel with auto-resume after 5 seconds
  const pauseCarouselWithResume = useCallback(() => {
    setIsCarouselPaused(true);

    // Clear any existing timer
    if (carouselTimerRef.current) {
      clearTimeout(carouselTimerRef.current);
    }

    // Set new timer to resume after 5 seconds
    carouselTimerRef.current = setTimeout(() => {
      setIsCarouselPaused(false);
    }, 5000);
  }, []);

  // Handle manual capability selection
  const handleCapabilityClick = useCallback(
    (index: number) => {
      pauseCapabilitiesWithResume();
      setActiveCapability(index);
      setProgress(0);
    },
    [pauseCapabilitiesWithResume],
  );

  const nextFeature = useCallback(() => {
    pauseCarouselWithResume();
    setActiveFeatureIndex((prev) => (prev + 1) % features.length);
  }, [pauseCarouselWithResume]);

  const prevFeature = useCallback(() => {
    pauseCarouselWithResume();
    setActiveFeatureIndex(
      (prev) => (prev - 1 + features.length) % features.length,
    );
  }, [pauseCarouselWithResume]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (capabilitiesTimerRef.current) {
        clearTimeout(capabilitiesTimerRef.current);
      }
      if (carouselTimerRef.current) {
        clearTimeout(carouselTimerRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* CONTAINED CUBE STRUCTURE */}
      <div className="border border-neutral-200">
        {/* SECTION 1: FEATURE CUBES - Horizontal Carousel on Mobile */}
        <div className="border-b border-neutral-200">
          {/* Mobile: Horizontal scrolling carousel */}
          <div className="relative overflow-hidden sm:hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(${activeFeatureIndex * 100}%)` }}
            >
              {features.map((feature, _index) => (
                <div
                  key={feature.title}
                  className="flex min-h-[280px] w-full flex-shrink-0 flex-col px-6 py-8"
                >
                  <h2 className="mb-2 text-sm font-semibold text-neutral-900">
                    {feature.title}
                  </h2>
                  <p className="mb-4 text-xs leading-relaxed text-neutral-500">
                    {feature.description}
                  </p>
                  <div className="flex-grow" />
                  <div className="mt-4 h-32 w-full">
                    <feature.icon />
                  </div>
                </div>
              ))}
            </div>
            {/* Navigation arrows */}
            <button
              onClick={prevFeature}
              aria-label="תכונה קודמת"
              className="absolute top-1/2 left-2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-none border border-neutral-200 bg-white/90 text-neutral-600 shadow-lg transition-transform hover:border-neutral-400 active:scale-95"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={nextFeature}
              aria-label="תכונה הבאה"
              className="absolute top-1/2 right-2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-none border border-neutral-200 bg-white/90 text-neutral-600 shadow-lg transition-transform hover:border-neutral-400 active:scale-95"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            {/* Square indicator */}
            <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
              {features.map((_, _index) => (
                <div
                  key={_index}
                  className={`h-2 w-2 rounded-none transition-colors ${_index === activeFeatureIndex ? "bg-neutral-900" : "bg-neutral-300"}`}
                />
              ))}
            </div>
          </div>
          {/* Desktop: Grid layout */}
          <div className="hidden sm:grid sm:grid-cols-2 md:grid-cols-3">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`flex min-h-[200px] flex-col p-4 sm:min-h-[260px] sm:p-5 ${
                  index < 2
                    ? "border-b border-neutral-200 sm:border-b md:border-b-0 md:border-l"
                    : ""
                } ${index === 0 ? "sm:col-span-2 md:col-span-1" : ""}`}
              >
                <h2 className="mb-2 text-sm font-semibold text-neutral-900">
                  {feature.title}
                </h2>
                <p className="mb-4 text-xs leading-relaxed text-neutral-500">
                  {feature.description}
                </p>
                <div className="flex-grow" />
                <div className="mt-4 h-28 w-full sm:h-32 md:h-36">
                  <feature.icon />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SECTION 3: CAPABILITIES + VISUAL - Buttons take more space on mobile */}
        <div className="relative z-10 grid grid-cols-[1.5fr_1fr] border-b border-neutral-200 bg-white md:grid-cols-2">
          {/* LEFT: Capabilities list - Takes more space (60%) on mobile - order first in RTL */}
          <div className="relative z-10 order-1 border-r border-b-0 border-neutral-200 p-4 sm:p-4 md:p-6">
            <h2 className="mb-4 text-base font-bold text-neutral-900 sm:mb-4 sm:text-base md:mb-6 md:text-lg">
              מה אפשר לעשות
            </h2>
            <div className="relative space-y-3 sm:space-y-3">
              {capabilities.map((cap, index) => {
                const isActive = activeCapability === index;
                return (
                  <button
                    key={cap.label}
                    type="button"
                    onClick={() => handleCapabilityClick(index)}
                    className={`relative z-20 flex w-full cursor-pointer items-start gap-2 overflow-hidden p-3 text-right transition-all sm:gap-3 sm:p-3 ${
                      isActive
                        ? "border border-neutral-200 bg-neutral-100"
                        : "border border-transparent hover:bg-neutral-50"
                    }`}
                  >
                    {isActive && (
                      <div
                        className="absolute right-0 bottom-0 h-0.5 bg-neutral-900 transition-all duration-75 ease-linear"
                        style={{ width: `${progress}%` }}
                      />
                    )}
                    <div className="pointer-events-none min-w-0 flex-1">
                      <h3
                        className={`text-sm font-semibold sm:text-sm ${
                          isActive ? "text-neutral-900" : "text-neutral-700"
                        }`}
                      >
                        {cap.label}
                      </h3>
                      <p className="mt-1 hidden text-xs text-neutral-500 sm:block">
                        {cap.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* RIGHT: Dynamic visual icon display - Takes less space (40%) on mobile - order second in RTL */}
          <div className="relative z-10 order-2 flex min-h-[140px] items-center justify-center border-l border-neutral-200 bg-neutral-50 p-2 sm:min-h-[160px] sm:p-4 md:min-h-[200px] md:p-6">
            <div className="h-[80px] w-full max-w-[120px] sm:h-[120px] sm:max-w-[240px] md:h-[140px] md:max-w-[280px]">
              <ActiveVisualIcon />
            </div>
          </div>
        </div>

        {/* SECTION 4: BETA ACCESS - Enhanced CTA */}
        <div className="border-b border-neutral-200 bg-gradient-to-br from-neutral-900 to-neutral-800">
          <div className="flex flex-col items-center gap-4 p-6 text-center sm:p-8 md:p-10">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 flex-shrink-0 animate-pulse rounded-full bg-white"></span>
              <span className="text-xs font-medium tracking-wide text-white uppercase">כרגע בבטא סגורה</span>
            </div>

            <h3 className="max-w-md text-xl leading-tight font-bold text-white sm:text-2xl md:text-3xl">
              רוצים גישה מוקדמת ל־API?
            </h3>

            <a
              href="/contact?source=api-beta"
              className="mt-2 inline-flex items-center gap-2 border border-white bg-white px-6 py-3 text-sm font-semibold text-neutral-900 transition-all hover:scale-105"
            >
              השאירו פרטים — נחזור אליכם תוך 24 שעות
              <ChevronLeft className="h-4 w-4" />
            </a>
          </div>
        </div>


      </div>
    </>
  );
}
